import React from 'react';
import { useApp } from '../context/AppContext';
import EquipmentTable from '../components/EquipmentTable';
import FolderTree from '../components/FolderTree';
import CartSidebar from '../components/CartSidebar';
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { cn } from "@/lib/utils";

// Custom hook for media query
function useMediaQuery(query) {
  const [matches, setMatches] = React.useState(() => window.matchMedia(query).matches);
  React.useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) setMatches(media.matches);
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);
  return matches;
}

const QuickRent = () => {
  const { rentmanData: data, loading, isCartOpen, toggleCart, isCartPinned, setIsCartPinned } = useApp();
  const [selectedFolderId, setSelectedFolderId] = React.useState(null);
  const { cartItems } = useCart();

  // Only shrink layout if pinned and screen < 1400px
  const isNarrowScreen = useMediaQuery('(max-width: 1299px)');
  const isWideScreen = useMediaQuery('(min-width: 1300px)');
  const shouldShrink = isCartPinned && isNarrowScreen;
  const shouldPad = isCartOpen && isCartPinned && isWideScreen;

  const handleFolderClick = (folderId) => {
    setSelectedFolderId(folderId);
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 p-4">
          <h1 className="text-2xl font-bold mb-4">Quick Rent</h1>
          <div className="flex items-center justify-center h-[calc(100vh-var(--header-height)-2rem)]">
            <div className="text-center">
              <p className="font-medium">Loading equipment data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-var(--header-height))]">
      <div className={cn(
        "flex-1 p-4 transition-all duration-300 ease-in-out",
        shouldShrink && "pr-[616px]"
      )}>
        {/* Mobile Layout */}
        <div className="flex flex-col h-full md:hidden">
          {/* Folder Tree - Full width on mobile */}
          <div className="w-full mb-4 flex-shrink-0">
            <div className="mb-2">
              <h2 className="text-lg font-semibold">Folders</h2>
            </div>
            <Separator className="mb-2" />
            <ScrollArea className="h-40 w-50"> {/* Gave the folder tree a fixed height on mobile */}
              <FolderTree data={data} onFolderClick={handleFolderClick} />
            </ScrollArea>
          </div>

          {/* --- THIS IS THE FIX --- */}
          {/* This container now has `flex-1` to take up remaining space, and `relative` for positioning context */}
          <div className="flex-1 relative">
            <EquipmentTable
              data={data}
              scrollToId={selectedFolderId}
              height="100%"
            />
          </div>
        </div>

        {/* Desktop Layout */}
        <div className={cn(
          "hidden md:flex h-full w-full transition-all duration-300 ease-in-out",
          isCartPinned && isNarrowScreen ? "flex-col" : "flex-row"
        )}>
          {/* Folder Tree - Fixed width container */}
          <div className={cn(
            isCartPinned && isNarrowScreen
              ? "w-full lg:w-full xl:w-full border-r-0 pr-0 mb-4"
              : "w-64 lg:w-72 xl:w-80 border-r border-[hsl(var(--border-yellow))] pr-4 mb-0"
          )}>
            <ScrollArea className={isCartPinned && isNarrowScreen ? "h-[35vh]" : "h-full"}>
              <FolderTree data={data} onFolderClick={handleFolderClick} />
            </ScrollArea>
          </div>

          {/* Equipment Table - Flexible container */}
          <div className={cn(
            isCartPinned && isNarrowScreen
              ? "w-full pl-0"
              : cn("flex-1 pl-4 h-full", shouldPad && "pr-[600px]")
          )}>
            <EquipmentTable
              data={data}
              scrollToId={selectedFolderId}
              height="100%"
            />
          </div>
        </div>
      </div>

      {/* Cart Sidebar */}
      <CartSidebar
        isOpen={isCartOpen}
        onClose={toggleCart}
        onPinChange={setIsCartPinned}
      />
    </div>
  );
};

export default QuickRent;
